import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios'
import { produce } from 'immer'
import { Logger } from '@utils'
import { ApiResponse, ApiRequest } from '@api'
import config from '@configs'

// type
import { ReqType, ResType } from './types'

export class Api {
	axiosDefaultInfo: AxiosRequestConfig = {
		baseURL: config.get('api').url,
		headers: {
			Authorization: '',
		},
	}
	req: ReqType = new ApiRequest({})
	res: ResType

	constructor(req: ReqType) {
		this.req = Object.assign({}, req)
		this.res = new ApiResponse({})

		if (req.baseURL !== undefined && req.baseURL !== '') {
			const newAxiosDefaultInfo: AxiosRequestConfig = {
				...this.axiosDefaultInfo,
				baseURL: req.baseURL,
			}
			this.axiosDefaultInfo = newAxiosDefaultInfo
		}
	}

	static async connection(req: ReqType): Promise<ResType> {
		const apiProvider = new Api(req)
		const result: ResType | undefined = await apiProvider.transaction()

		return result ?? new ApiResponse({})
	}

	getRequest() {
		this.req.accessToken = this.getAccessToken()
		this.req.headers = this.getHeaders()

		return this.req
	}

	getAccessToken(): string {
		const result: string = localStorage.getItem('accessToken') || ''

		return result
	}

	getHeaders(): ReqType['headers'] {
		try {
			const defaultHeader: { headers: ReqType['headers'] } =
				this.req.accessToken === ''
					? { headers: {} }
					: { headers: { Authorization: `Bearer ${this.req.accessToken}` } }

			const newHeaders: ReqType['headers'] = produce(
				defaultHeader,
				(draft: { headers: ReqType['headers'] }) => {
					const newHeaders = Object.assign(draft.headers, this.req.customHeaders)
					draft.headers = { ...newHeaders }
				},
			)

			const result: ReqType['headers'] = { ...newHeaders }

			return result
		} catch (error) {
			Logger.warn(error)

			return { headers: {} }
		}
	}

	queryStringGenerator(): string {
		try {
			const queryKeyArr: string[] = Object.keys(this.req.query)
			const queryPaireArr: string[] = queryKeyArr.map((aKey: string, idx: number) => {
				const value: string = this.req.query[aKey]
				return idx === 0 ? `${aKey}=${value}` : `&${aKey}=${value}`
			})

			const result: string = queryKeyArr.length > 0 ? '?' + queryPaireArr.join('') : ''

			return result
		} catch (error) {
			Logger.warn(error)

			return ''
		}
	}

	async transaction(): Promise<ResType | undefined> {
		try {
			if (this.getRequest()) {
				const axiosCreator = axios.create(this.axiosDefaultInfo)
				const queryString = this.req.query === null ? '' : this.queryStringGenerator()
				const uri = `${this.req.uri}${queryString}`
				const body: any = this.req.body
				const headers: AxiosRequestConfig = this.req.headers
				const method: ReqType['method'] = this.req.method

				const result: ResType | undefined =
					method === 'get' || method === 'delete'
						? await axiosCreator[method](uri, headers)
								.then(async (res: AxiosResponse) => {
									return await this.transactionSuccess(res)
								})
								.catch(async (error: AxiosError) => {
									return await this.transactionFail(error)
								})
						: await axiosCreator[method](uri, body, headers)
								.then(async (res: AxiosResponse) => {
									return await this.transactionSuccess(res)
								})
								.catch(async (error: AxiosError) => {
									return await this.transactionFail(error)
								})
				return result
			} else {
				throw new Error('')
			}
		} catch (error) {
			Logger.warn(error)

			return undefined
		}
	}

	async transactionSuccess(res: AxiosResponse): Promise<ResType | undefined> {
		const response: ResType = new ApiResponse({ ...res })

		this.res = { ...response } as ResType
		return await this.responseHandler()
	}

	async transactionFail(error: AxiosError): Promise<ResType | undefined> {
		/* eslint-disable */
		const newException = error.response!.data as any as ResType['exception']
		const response: ResType =
			error.response !== undefined
				? new ApiResponse({
						data: [],
						status: error.response.status,
						statusText: error.response.statusText,
						headers: error.response.headers,
						config: error.response.config,
						request: error.response.request as object,
						exception: newException,
				  })
				: new ApiResponse({ ...(this.res as AxiosResponse) })

		this.res = { ...response }
		return await this.responseHandler()
	}

	async responseHandler(): Promise<ResType | undefined> {
		try {
			const result: ResType =
				this.req.isRefresh && this.res.status === 401
					? await this.updateTokenAndReconnect()
					: this.res

			if ([200, 201].includes(result.status)) {
				Logger.api('success', this.req, result)
			} else if (result.status < 500) {
				Logger.api('fail', this.req, result)
			} else {
				Logger.api('error', this.req, result)
			}

			return result
		} catch (error) {
			Logger.warn(error)

			return undefined
		}
	}

	// async updateTokenAndReconnect(): Promise<ResType> {
	async updateTokenAndReconnect() {
		const newAccessToken: string = await this.getAccessTokenUsingTokenRefresh()

		if (newAccessToken !== 'refresh_fail') {
			const axiosCreator = axios.create(this.axiosDefaultInfo)
			const queryString = this.req.query === null ? '' : this.queryStringGenerator()
			const uri = `${this.req.uri}${queryString}`
			const body: any = this.req.body
			const headers: AxiosRequestConfig = {
				headers: { Authorization: `Bearer ${newAccessToken}` },
			}
			const method: ReqType['method'] = this.req.method

			const newReq: ReqType = produce<ReqType>(this.req, draft => {
				draft['accessToken'] = newAccessToken
				draft['headers'] = headers

				if (uri === '/auth/login-using-access-token') {
					draft.body = {
						accessToken: newAccessToken,
					}
				}
			})

			this.req = new ApiRequest({ ...newReq })

			const result: ResType | undefined =
				method === 'get' || method === 'delete'
					? await axiosCreator[method](uri, headers)
							.then(async (res: AxiosResponse) => {
								const response: ResType = new ApiResponse({ ...res })
								return response
							})
							.catch(async (error: AxiosError) => {
								const newException = error.response!.data as any as ResType['exception']
								const response: ResType =
									error.response !== undefined
										? new ApiResponse({
												data: [],
												status: error.response.status,
												statusText: error.response.statusText,
												headers: error.response.headers,
												config: error.response.config,
												request: error.response.request as object,
												exception: newException,
										  })
										: new ApiResponse({ ...(this.res as AxiosResponse) })
								return response
							})
					: await axiosCreator[method](uri, body, headers)
							.then(async (res: AxiosResponse) => {
								const response: ResType = new ApiResponse({ ...res })
								return response
							})
							.catch(async (error: AxiosError) => {
								const newException = error.response!.data as any as ResType['exception']
								const response: ResType =
									error.response !== undefined
										? new ApiResponse({
												data: [],
												status: error.response.status,
												statusText: error.response.statusText,
												headers: error.response.headers,
												config: error.response.config,
												request: error.response.request as object,
												exception: newException,
										  })
										: new ApiResponse({ ...(this.res as AxiosResponse) })
								return response
							})
			return result
		} else {
			return new ApiResponse({ ...(this.res as AxiosResponse) })
		}
	}

	async getAccessTokenUsingTokenRefresh(): Promise<string> {
		const tokenRefreshResult: ResType = await this.tokenRefresh()

		if (tokenRefreshResult.status === 201) {
			const newAccessToken: string = tokenRefreshResult.data[0].accessToken
			const newRefreshToken: string = tokenRefreshResult.data[0].refreshToken

			localStorage.setItem('accessToken', newAccessToken)
			localStorage.setItem('refreshToken', newRefreshToken)

			return newAccessToken
		} else {
			localStorage.setItem('accessToken', '')
			localStorage.setItem('refreshToken', '')
			localStorage.setItem('isLogin', 'false')

			if (!['/auth/loading', '/auth/login', '/'].includes(window.location.pathname)) {
				window.location.assign('/auth/loading')
			}
			return 'refresh_fail'
		}
	}

	async tokenRefresh(): Promise<ResType> {
		const refreshToken: string = localStorage.getItem('refreshToken') || ''

		if (refreshToken === '') {
			return new ApiResponse({ ...(this.res as AxiosResponse), status: 401 })
		}

		const req: ReqType = new ApiRequest({
			method: 'post',
			uri: '/auth/token-refresh',
			body: {
				refreshToken: refreshToken,
			},
		})

		const axiosCreator = axios.create(this.axiosDefaultInfo)
		const result: ResType | undefined = await axiosCreator
			.post(req.uri, req.body, {})
			.then(async (res: AxiosResponse) => {
				const response: ResType = new ApiResponse({ ...res })
				Logger.api('success', req, res as ResType)
				return response
			})
			.catch(async (error: AxiosError) => {
				/* eslint-disable */
				const newException = error.response!.data as any as ResType['exception']
				const response: ResType =
					error.response !== undefined
						? new ApiResponse({
								data: [],
								status: error.response.status,
								statusText: error.response.statusText,
								headers: error.response.headers,
								config: error.response.config,
								request: error.response.request as object,
								exception: newException,
						  })
						: new ApiResponse({ ...req })

				Logger.api('fail', req, response)

				return { ...response }
			})

		if (result === undefined) {
			return new ApiResponse({ ...req })
		}

		return result
	}

	static createFormData(body: { [key: string]: any }, files: File[]) {
		const res = new FormData()

		files.forEach(aFile => {
			res.append('files', aFile)
		})

		Object.keys(body).forEach(key => {
			res.append(key, body[key])
		})

		return res
	}
}
